
import React from 'react';
import { useDispatch } from 'react-redux';
import { markNotificationRead, markNotificationUnread } from '../../../actions/notifications';
import { Box, Typography } from '@mui/material';
import { formatDistanceToNow, differenceInCalendarDays } from 'date-fns';
import { toZonedTime } from 'date-fns-tz'; // SLT-safe day math
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InventoryIcon from '@mui/icons-material/Inventory2';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventIcon from '@mui/icons-material/Event';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AlarmIcon from '@mui/icons-material/Alarm';
import WarningIcon from '@mui/icons-material/Warning';
import {
  StyledCard,
  StyledUnreadCard,
  StyledContent,
 } from './styles';

const TIMEZONE = 'Asia/Colombo';

const STOCK_SEVERITY_COLORS = {
  OUT_OF_STOCK:   '#EF5350',  // red
  CRITICALLY_LOW: '#ff5c00',  // orange
  LOW_STOCK:      '#ffc800',  // yellow
};

const STOCK_SEVERITY_BG_COLORS = {
  OUT_OF_STOCK:   'rgba(239, 83, 80, 0.1)',  // red
  CRITICALLY_LOW: 'rgba(255,92,0, 0.125)',  // orange
  LOW_STOCK:      'rgba(255, 200, 0, 0.2)',  // yellow
};

const STOCK_TEXT_LABELS = {
  OUT_OF_STOCK:   'Out of Stock!',
  CRITICALLY_LOW: 'Critically Low',
  LOW_STOCK:      'Low Stock',
};

// NEW: group types
const EXPIRY_TYPES = new Set([
  'EXPIRING_6M',
  'EXPIRING_3M',
  'EXPIRING_1M',
  'EXPIRING_1W',
  'EXPIRING_1D',
  'EXPIRED',
]);

const STOCK_TYPES = new Set([
  'LOW_STOCK',
  'CRITICALLY_LOW',
  'OUT_OF_STOCK',
]);

// EXPIRY LABELS (unchanged)
const typeLabels = {
  EXPIRING_6M: 'Expires within 6 Months',
  EXPIRING_3M: 'Expires within 3 Months',
  EXPIRING_1M: 'Expires within 1 Month',
  EXPIRING_1W: 'Expires within 1 Week',
  EXPIRING_1D: 'Expires within 1 Day',
  EXPIRED:     'Expired',
};

const EXPIRY_SEVERITY_COLORS = {
  EXPIRED:       '#ff0000c2',   // same as dashboard
  EXPIRING_1D:   '#ff3400c2',
  EXPIRING_1W:   '#ff8904',
  EXPIRING_1M:   '#ffba00',
  EXPIRING_3M:   '#ffda00',
  EXPIRING_6M:   '#c1cd32'
};

const dateLabels = {
  EXPIRING_6M: 'will expire within 6 Months',
  EXPIRING_3M: 'will expire within 3 Months',
  EXPIRING_1M: 'will expire within 1 Month',
  EXPIRING_1W: 'will expire within 1 Week',
  EXPIRING_1D: 'will expire within 1 Day',
  EXPIRED:     'has expired',
};

// NEW: stock labels
const stockTypeLabels = {
  LOW_STOCK:      'Low Stock',
  CRITICALLY_LOW: 'Critically Low',
  OUT_OF_STOCK:   'Out of Stock',
};

const getExpiryIcon = (type) => {
  switch (type) {
    case 'EXPIRED':
      return <WarningIcon sx={{ fontSize: 26, color: 'white' }} />;

    case 'EXPIRING_1D':
      return <AlarmIcon sx={{ fontSize: 26, color: 'white' }} />;

    case 'EXPIRING_1W':
      return <HourglassBottomIcon sx={{ fontSize: 26, color: 'white' }} />;

    case 'EXPIRING_1M':
      return <EventIcon sx={{ fontSize: 26, color: 'white' }} />;

    case 'EXPIRING_3M':
      return <CalendarMonthIcon sx={{ fontSize: 26, color: 'white' }} />;
    case 'EXPIRING_6M':
      return <InventoryIcon sx={{ fontSize: 26, color: 'white' }} />;
    default:
      return <CalendarMonthIcon sx={{ fontSize: 26, color: 'white' }} />;
  }
};

const getStockIcon = (type) => {
  switch (type) {
    case 'OUT_OF_STOCK':
      return <ErrorIcon sx={{ fontSize: 28, color: 'white' }} />;
    case 'CRITICALLY_LOW':
      return <WarningIcon sx={{ fontSize: 28, color: 'white' }} />;
    case 'LOW_STOCK':
    default:
      return <RemoveCircleIcon sx={{ fontSize: 32, color: 'white' }} />;
  }
};


function daysLeftSLT(expiryDate) {
  if (!expiryDate) return null;
  const nowSLT    = toZonedTime(new Date(), TIMEZONE);
  const expirySLT = toZonedTime(new Date(expiryDate), TIMEZONE);
  return differenceInCalendarDays(expirySLT, nowSLT);
}

const Notification = ({ notification }) => {
  const read       = !!notification?.read;
  const sentAt     = notification?.sentAt ?? Date.now();
  const type       = notification?.type ?? 'EXPIRED';

  const inv  = notification?.inventoryId || null;
const item = inv?.itemId || null;
const med  = item?.medicineId || null;

// product name OR medicine brand/generic
const brandName =
  item?.name ||
  med?.brandName ||
  med?.genericName ||
  'Unknown item';

const genericName = med?.genericName ?? '';

  const batchNo     = inv?.batchNo ?? '—';
  const expiryDate  = inv?.expiryDate ?? null;

  const relativeTime = formatDistanceToNow(new Date(sentAt), { addSuffix: true });

  // NEW: figure out which kind it is
  const isExpiry = EXPIRY_TYPES.has(type);
  const isStock  = STOCK_TYPES.has(type);

  let label = '';
  let dateText = '';

  if (isExpiry) {
    const labelBase = typeLabels[type] ?? '';
    dateText        = dateLabels[type] ?? '';
    const dLeft     = daysLeftSLT(expiryDate);

    label =
      type === 'EXPIRED'
        ? labelBase
        : (Number.isFinite(dLeft)
            ? `${labelBase}\n(${dLeft === 1 ? '1 day' : `${dLeft} days`})`
            : labelBase);
  } else if (isStock) {
    const base   = stockTypeLabels[type] ?? 'Stock Alert';
    const qty    = notification?.quantity ?? inv?.quantity;
    const prefix = 'Stock Alert · ';
    label        = qty != null ? `${prefix}${base} (${qty} left)` : `${prefix}${base}`;
  } else {
    // fallback (shouldn't really happen)
    label = 'Alert';
  }

  const Root = read ? StyledCard : StyledUnreadCard;
  const dispatch = useDispatch();
  const isRead = !!notification?.read;

  const handleClick = () => {
    if (isRead) dispatch(markNotificationUnread(notification._id));
    else        dispatch(markNotificationRead(notification._id));
  };

  return (
    <Root onClick={handleClick} style={{ cursor: 'pointer' }}>
      <StyledContent sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.25, sm: 0 },
          }}
        >
          <Box
  sx={{
    textAlign: {xs:'center', sm:'left'},
    width: { xs: '100%', sm: '70%' },
    // ml: { xs: '20px', sm: '20px' },
    mr: 'auto',
  }}
>

            <Typography
              variant="body2"
              sx={{
                display: 'inline-block',
                color:'#1976d2',
                background:'rgba(25, 118, 210, 0.1)',
                border:'1px solid #1976d2',
                padding:'4px 10px',
                borderRadius:'5px'
              }}
            >
              {batchNo}
            </Typography>

            <Typography
              variant="subtitle1"
              sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '16px' }}
            >
              {brandName}
            </Typography>

            {genericName && (
              <Typography variant="body2" sx={{ fontSize: '13px', color:'grey' }}>
                {genericName}
              </Typography>
            )}

            {!inv && (
              <Typography variant="caption" color="warning.main">
                (inventory no longer available)
              </Typography>
            )}
          </Box>
<Box sx={{ width: { xs: '100%', sm: 150 }, display: 'flex', justifyContent: 'center' }}>
          {/* OPTIONAL: pass kind if you want different shapes in styles.js */}
          {isExpiry && (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minWidth: 70,
    }}
  >
    {/* Circle icon */}
    <Box
      sx={{
        width: 42,
        height: 42,
        borderRadius: '20%',
        background: EXPIRY_SEVERITY_COLORS[type],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {getExpiryIcon(type)}
    </Box>

    {/* Text label */}
    <Typography
      sx={{
        marginTop: '4px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#333',
        textAlign: 'center',
        whiteSpace: 'pre-line',
      }}
    >
      {label} {/* e.g. "Expires in 7 days" */}
    </Typography>
  </Box>
)}


{isStock && (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      // mr: '20px',
      minWidth: 70
    }}
  >
    {/* Circle icon */}
    <Box
      sx={{
        width: 42,
        height: 42,
        // border: `2px solid ${STOCK_SEVERITY_BG_COLORS[type]}`,
        backgroundColor:STOCK_SEVERITY_BG_COLORS[type],
        borderRadius: '50%',
        // background: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {React.cloneElement(getStockIcon(type), {
        sx: { fontSize: 26, color: STOCK_SEVERITY_COLORS[type]}
      })}
    </Box>

    {/* Text label */}
    <Typography
      sx={{
        marginTop: '4px',
        fontSize: '12px',
        fontWeight: 600,
        color: '#333',
        textAlign: 'center'
      }}
    >
      {STOCK_TEXT_LABELS[type]}
    </Typography>
  </Box>
)}
</Box>
        </Box>

        <Box
  sx={{
    display: 'flex',
    width: '100%',
    mt: 1,
    gap: 1,
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: { xs: 'flex-start', sm: 'center' },
    justifyContent: { xs: 'flex-start', sm: 'space-between' },
  }}
>

          <Typography sx={{ textAlign: {xs:'center', sm:'left'}, fontSize: { xs: 13, sm: 14 } }}>
            {isExpiry && (
              <>Batch number {batchNo} containing {brandName} {dateText}.</>
            )}
            {isStock && (
              <>
                {notification?.message
                  || `Batch number ${batchNo} containing ${brandName} is ${stockTypeLabels[type]?.toLowerCase() || 'low in stock'}.`}
              </>
            )}
          </Typography>

          <Typography
  sx={{
    fontSize: { xs: 12, sm: 13 },
    fontWeight: 'bold',
    color: 'silver',
    width: { xs: '100%', sm: 150 },
    textAlign: 'center',
    pl: { xs: 0, sm: 0 },
  }}
>
  {relativeTime}
</Typography>

        </Box>
      </StyledContent>
    </Root>
  );
};

export default Notification;
